/**
 * Agent API — conversation history + multi-thread management.
 */

import type { LangChainMessage } from '@assistant-ui/react-langgraph'
import type { ThreadStatus } from '@/features/agent/ThreadStatusContext'
import { ApiError, apiClient } from '@/services/api/client'

export interface AgentHistoryResponse {
  messages: LangChainMessage[]
  thread_id: string | null
  /** ID of the oldest message in this page; pass back as `before` to load
   * the next older page. `null` when no more pages exist. */
  next_cursor?: string | null
  has_more?: boolean
}

export interface AgentOptions {
  disabled_tools: string[]
  deep_mode: boolean
}

export interface AgentToolInfo {
  name: string
  description: string
}

export interface AgentThreadHealth {
  tokens_used: number
  budget_tokens: number
  /** Non-null when the thread is already over budget or would be trimmed. */
  status: ThreadStatus | null
  /** Per-thread agent settings (tools panel + deep mode). Null = defaults. */
  agent_options?: AgentOptions | null
}

export async function getThreadHealth(
  caseId: string,
  threadId: string
): Promise<AgentThreadHealth> {
  return apiClient.get(`/agent/${caseId}/threads/${threadId}/health`)
}

export type AgentThreadKind = 'chat' | 'hypothesis_run'

export interface AgentThreadItem {
  id: string
  title: string | null
  created_at: string
  updated_at: string
  /** 'chat' for interactive threads, 'hypothesis_run' for autonomous-run
   * threads. Defaults to 'chat' for backwards compatibility on rows
   * created before the kind column existed. */
  kind: AgentThreadKind
  /** When kind='hypothesis_run', points at the parent hypothesis so the
   * UI can navigate to its detail page. Null for chat threads. */
  hypothesis_id: string | null
}

export async function getAgentHistory(
  caseId: string,
  threadId: string,
  opts?: { limit?: number; before?: string }
): Promise<AgentHistoryResponse> {
  const params: Record<string, string | number> = {}
  if (opts?.limit) params.limit = opts.limit
  if (opts?.before) params.before = opts.before
  try {
    return await apiClient.get<AgentHistoryResponse>(
      `/agent/${caseId}/threads/${threadId}/history`,
      { params }
    )
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      return { messages: [], thread_id: null }
    }
    throw err
  }
}

export async function listThreads(
  caseId: string,
  kind?: AgentThreadKind
): Promise<AgentThreadItem[]> {
  return apiClient.get(`/agent/${caseId}/threads`, { params: kind ? { kind } : undefined })
}

export async function createThread(caseId: string): Promise<AgentThreadItem> {
  return apiClient.post(`/agent/${caseId}/threads`)
}

export async function deleteThread(caseId: string, threadId: string): Promise<void> {
  await apiClient.delete(`/agent/${caseId}/threads/${threadId}`)
}

export async function renameThread(
  caseId: string,
  threadId: string,
  title: string
): Promise<AgentThreadItem> {
  return apiClient.patch(`/agent/${caseId}/threads/${threadId}`, { title })
}

export async function getAgentTools(): Promise<{ tools: AgentToolInfo[] }> {
  return apiClient.get('/agent/tools')
}

export async function updateThreadOptions(
  caseId: string,
  threadId: string,
  options: AgentOptions
): Promise<AgentThreadItem> {
  return apiClient.patch(`/agent/${caseId}/threads/${threadId}`, { agent_options: options })
}

export const agentApi = {
  getAgentHistory,
  getThreadHealth,
  listThreads,
  createThread,
  deleteThread,
  renameThread,
}
